import type { JSX } from 'react';
import { AlertTriangle, Clapperboard, FileText, HelpCircle, ShieldAlert, UserCheck, Users } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * What the contact-sheet check saw — the influencer/faceless call.
 *
 * ── The rule this component exists to enforce ──────────────────────────────
 * A null verdict means **NOT MEASURED**, and it renders as NOTHING. It does not
 * mean faceless. Nulls are the common case (any search run without the "Face
 * creators only" box ticked never asks the question), and they also cover the
 * failure modes: expired thumbnails, an account too new to have nine posts, a
 * provider outage. Rendering any of those as "faceless" would put a verdict on
 * screen that nobody actually made, next to a creator who might be perfect.
 *
 * The positive case is the point of the badge — it says "we looked at their
 * thumbnails and a real person fronts this account", which is a claim no other
 * column here can make. `accountType` is guessed from the handle and bio;
 * this was read off the video.
 */

type Meta = {
  label: string;
  icon: typeof UserCheck;
  tone: 'good' | 'bad' | 'muted';
  hint: string;
};

const PRESENTER_META: Record<string, Meta> = {
  recurring_person: {
    label: 'Face',
    icon: UserCheck,
    tone: 'good',
    hint: 'A consistent person fronts this account, seen across their recent thumbnails.',
  },
  multiple_people: {
    label: 'Faces',
    icon: Users,
    tone: 'good',
    hint: 'A consistent duo or small group fronts this account — a couple, family or pair.',
  },
  varied_footage: {
    label: 'Clips',
    icon: Clapperboard,
    tone: 'bad',
    hint: "Different subjects in every thumbnail — reposted or edited footage, not the account owner's own.",
  },
  no_people: {
    label: 'Faceless',
    icon: FileText,
    tone: 'bad',
    hint: 'Nobody on camera — slideshows, lyrics, gameplay or stock footage.',
  },
  unclear: {
    label: 'Unclear',
    icon: HelpCircle,
    tone: 'muted',
    hint: 'The thumbnails were too ambiguous to call either way.',
  },
};

/** Human labels for the framing, used in the tooltip. */
const FRAMING: Record<string, string> = {
  face_to_camera: 'face to camera',
  full_body: 'full body',
  mixed: 'mixed framing',
  none: 'no presenter on camera',
};

export interface PresenterProfile {
  audienceAgeBand?: string | null;
  setting?: string | null;
  productionQuality?: string | null;
  categories?: string[];
  brandSafety?: string[];
}

/** Flags a brand would want to know about before briefing someone. */
const SAFETY_LABELS: Record<string, string> = {
  profanity_on_screen: 'on-screen profanity',
  suggestive: 'suggestive',
  alcohol_or_smoking: 'alcohol / smoking',
  violence_or_gore: 'violence',
  political: 'political',
};

const QUALITY_LABELS: Record<string, string> = {
  polished: 'polished production',
  mid: 'mid production',
  lo_fi: 'lo-fi / handheld',
};

export function PresenterBadge({
  presenter,
  confidence,
  presentation,
  formats,
  reason,
  profile,
}: {
  presenter: string | null;
  confidence?: number | null;
  presentation?: string | null;
  formats?: string[] | null;
  reason?: string | null;
  profile?: PresenterProfile | null;
}): JSX.Element | null {
  // Not measured. Render nothing — see the note at the top of this file.
  if (!presenter) return null;

  const meta = PRESENTER_META[presenter];
  if (!meta) return null;
  const Icon = meta.icon;
  const unsure = (confidence ?? 1) < 0.6;

  // Flags worth surfacing on the row itself, not just on hover — "none
  // apparent" is a reassurance, not a warning, so it never becomes a chip.
  const flags = (profile?.brandSafety ?? []).filter((f) => f !== 'none_apparent');
  const cats = profile?.categories ?? [];
  // Surfaced on the row, not just on hover: an account that reads teen must
  // never reach an age-restricted brief, and that is a decision made while
  // scanning the list rather than while hovering one row.
  const readsTeen = profile?.audienceAgeBand === 'likely_teen';

  const title = [
    `${meta.label}${confidence != null ? ` · ${Math.round(confidence * 100)}% confident` : ''}`,
    meta.hint,
    presentation && FRAMING[presentation] ? `Framing: ${FRAMING[presentation]}` : '',
    formats?.length ? `Looks like: ${formats.map(formatLabel).join(', ')}` : '',
    cats.length ? `About: ${cats.join(', ')}` : '',
    profile?.audienceAgeBand === 'likely_teen'
      ? 'Content reads as teen-made (school settings, uniforms) — not for age-restricted briefs'
      : profile?.audienceAgeBand === 'likely_adult'
        ? 'Content reads as adult-made'
        : '',
    profile?.setting && profile.setting !== 'unclear' ? `Films: ${profile.setting}` : '',
    profile?.productionQuality && QUALITY_LABELS[profile.productionQuality]
      ? QUALITY_LABELS[profile.productionQuality]
      : '',
    flags.length ? `Brand-safety: ${flags.map((f) => SAFETY_LABELS[f] ?? f).join(', ')}` : '',
    profile?.brandSafety?.includes('none_apparent') && !flags.length
      ? 'Brand-safety: nothing flagged'
      : '',
    reason ? `\n"${reason}"` : '',
    unsure ? '\nLow confidence — worth a look before acting on it.' : '',
  ]
    .filter(Boolean)
    .join('\n');

  return (
    <span className="inline-flex items-center gap-1" title={title}>
      <span
        className={cn(
          'inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-xs font-medium leading-none',
          meta.tone === 'good' && 'border-emerald-600/35 bg-emerald-600/10 text-emerald-700 dark:text-emerald-400',
          meta.tone === 'bad' && 'border-destructive/35 bg-destructive/10 text-destructive',
          meta.tone === 'muted' && 'border-border bg-muted/40 text-muted-foreground',
          unsure && 'opacity-70',
        )}
      >
        <Icon className="h-3 w-3 shrink-0" strokeWidth={2} />
        {meta.label}
        {unsure && <span className="opacity-70">?</span>}
      </span>
      {/* The formats are what the original ask was about — "find me GRWM /
          lipsync / talking-head creators" — so the top one is shown inline
          rather than buried in the tooltip. */}
      {meta.tone === 'good' && formats?.length ? (
        <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
          {formatLabel(formats[0]!)}
        </span>
      ) : null}
      {meta.tone === 'bad' && (
        <AlertTriangle className="h-3 w-3 shrink-0 text-destructive" aria-label="Not a face creator" />
      )}
      {readsTeen && (
        <span
          className="inline-flex items-center gap-0.5 rounded border border-amber-600/35 bg-amber-600/10 px-1 py-0.5 text-[10px] font-medium uppercase leading-none tracking-wide text-amber-700 dark:text-amber-500"
          title="The content reads as teen-made — school settings, uniforms, campus. Keep off age-restricted briefs."
        >
          teen
        </span>
      )}
      {flags.length > 0 && (
        <span
          className="inline-flex items-center gap-0.5 rounded border border-amber-600/35 bg-amber-600/10 px-1 py-0.5 text-[10px] font-medium leading-none text-amber-700 dark:text-amber-500"
          title={`Brand-safety: ${flags.map((f) => SAFETY_LABELS[f] ?? f).join(', ')}`}
        >
          <ShieldAlert className="h-2.5 w-2.5" />
          {flags.length}
        </span>
      )}
    </span>
  );
}

const FORMAT_LABELS: Record<string, string> = {
  talking_head: 'talking head',
  text_slides: 'text slides',
  grwm: 'GRWM',
  lipsync: 'lipsync',
  dance: 'dance',
  vlog: 'vlog',
  gameplay: 'gameplay',
  clips: 'clips',
  product: 'product',
  other: 'other',
};

function formatLabel(f: string): string {
  return FORMAT_LABELS[f] ?? f.replace(/_/g, ' ');
}
